import { ReactNode } from 'react';
import { View, ViewProps, Platform } from 'react-native';
import { useTheme } from '@/theme';

type CardVariant = 'elevated' | 'outlined' | 'subtle' | 'filled';
type CardElevation = 0 | 1 | 2 | 3;

type CardProps = ViewProps & {
  children: ReactNode;
  variant?: CardVariant;
  elevation?: CardElevation;
  padding?: number;
};

export function Card({ 
  children, 
  variant = 'elevated', 
  elevation = 1,
  padding,
  style, 
  ...rest 
}: CardProps) {
  const theme = useTheme();

  const getShadowStyle = (elev: CardElevation) => {
    switch (elev) {
      case 0:
        return {};
      case 1:
        return theme.shadows.level1;
      case 2:
        return theme.shadows.level2;
      case 3:
        return theme.shadows.level3;
      default:
        return theme.shadows.level1;
    }
  };

  const variantStyle = (() => {
    switch (variant) {
      case 'outlined':
        return {
          borderWidth: 1,
          borderColor: theme.colors.border,
          backgroundColor: theme.colors.surface,
        };
      case 'subtle':
        return {
          backgroundColor: theme.colors.background,
          borderWidth: 1,
          borderColor: theme.colors.borderMuted,
        };
      case 'filled':
        return {
          backgroundColor: theme.colors.primary + '10',
          borderWidth: 1,
          borderColor: theme.colors.primary,
        };
      default:
        return {
          backgroundColor: theme.colors.surface,
          ...getShadowStyle(elevation),
        };
    }
  })();

  const cardPadding = padding !== undefined ? padding : theme.spacing(3);

  return (
    <View
      {...rest}
      style={[
        {
          borderRadius: theme.radii.xl,
          padding: cardPadding,
          gap: theme.spacing(1.5),
        },
        variantStyle,
        style,
      ]}
    >
      {children}
    </View>
  );
}

